import { BrowserDXTPClientConnection } from 'core-ts/client';
import { DeferredPromise, ErrorUtils } from 'core-ts/common';
import NativeInterface from '../NativeInterface'

const SUPPORT_API_VERSION = 1;

export type RefreshTokenFunctionType = () => Promise<string> | null;

export interface LoginState {
    account?: string;
    accessToken?: string | null;
    pendingLogin: boolean;
}

export interface BackendConnectionOptions {
    maxReconnectCount?: number;
    reconnectDelayMs?: number;
    silentDisconnect?: boolean;
}

interface PendingRequest {
    type: string;
    data: any;
    promise: DeferredPromise<any>;
};

export class BackendConnection {
    private hostAddress: string;
    private loginState: LoginState;
    private connection: BrowserDXTPClientConnection;

    private loginPendingRequests: PendingRequest[] = [];
    private onErrorCallback: (hostAddress: string) => void;
    private refreshTokenFunc: RefreshTokenFunctionType;
    private options?: BackendConnectionOptions;

    constructor(hostAddress: string, 
                loginState: LoginState,
                refreshTokenFunc: RefreshTokenFunctionType,
                onErrorCallback: any,
                options?: BackendConnectionOptions)
    {
        console.log(`BackendConnection: ${hostAddress}`);
        this.hostAddress = hostAddress;
        this.loginState = { ...loginState, pendingLogin: loginState.pendingLogin ?? false }; 
        this.refreshTokenFunc = refreshTokenFunc;
        this.onErrorCallback = onErrorCallback;
        this.options = options;

        this.connection = new BrowserDXTPClientConnection({
            address: hostAddress,
            onConnectionOpen: this.onConnectionOpen.bind(this),
            onConnectionClose: this.onConnectionClose.bind(this),
            maxReconnectCount: options?.maxReconnectCount,
            reconnectDelayMs: options?.reconnectDelayMs,
            silentDisconnect: options?.silentDisconnect
        });
    }

    public getState() {
        return this.connection.getState();
    }

    public close() {
        this.connection.close();
    }

    private async onConnectionOpen() {
        console.log(`WebSocket connected to: ${this.hostAddress}`);
        this.sendApiVersion();

        if (this.loginState.accessToken) {
            try {
                await this.login(this.loginState.accessToken)
            } catch(e) {
                await this.login(await this.refreshTokenFunc())
            }
        }
    }

    private onConnectionClose() {
        if (!this.options?.silentDisconnect) {
            this.loginPendingRequests.forEach(value => value.promise.reject('Failed to login - disconnected'));
        }
        this.loginPendingRequests = [];
        this.onErrorCallback(this.hostAddress);
    }

    public async login(token?: string | null) {
        this.loginState.accessToken = token;
        return this.request("LOGIN", {access_token: token });
    }

    public async logout() {
        this.loginState.accessToken = undefined;
        this.loginState.account = undefined;
        return this.request("LOGOUT", null);
    }

    public setAccountId(id: string | undefined) {
        this.loginState.account = id;
    }

    public setPendingLogin(value: boolean) {
        this.loginState.pendingLogin = value;
        if (!this.loginState.pendingLogin) {
            console.log("Flushing pending requests");
            const pending = this.loginPendingRequests;
            this.loginPendingRequests = [];
            pending.forEach((item) => {
                this.request(item.type, item.data)
                .then((data) => item.promise.resolve(data))
                .catch((error) => item.promise.reject(error))
            });
        }
    }

    public async request(type: string, body: any): Promise<any> {
        if (this.loginState.pendingLogin &&
            type !== 'LOGIN' &&
            type !== 'LOGOUT' &&
            type !== 'SET_API_VERSION')
        {
            console.log(`Pending login, buffering request [${type}].`);
            const promise = new DeferredPromise();
            this.loginPendingRequests.push({type: type, data: body, promise: promise});
            return promise.promise();
        }

        const header: {[key: string]: any} = {
            type: type,
            app_instance: NativeInterface.getAppInstanceId(),
            account: this.loginState.account
        };

        console.log(`Sending request: [${JSON.stringify({header})}] [${JSON.stringify({body})}]`);
        return this.connection.sendRequest(header, body);
    }

    private sendApiVersion() {
        this.request("SET_API_VERSION", {version: SUPPORT_API_VERSION})
        .catch(error => {
            console.error(`SET_API_VERSION request failed: ${error}`);
            const errorCode = ErrorUtils.parseCode(error);
            if (errorCode === ErrorUtils.ERROR_API_VERSION) {
                NativeInterface.handleApiVersionError(error);
            }
        });
    }
}
